/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import FetchStatus from 'src/types/FetchStatus';
import { constructQueryParams, customFetch } from 'src/utils';
import { GetAllGroupsResponse, Group } from 'src/types/Groups';
import { GET_GROUPS_API } from 'src/constants/apiEndpoints';
import { getI18n } from 'react-i18next';
import { selectMarketplaceId } from './userInfo';
import type { AppState } from '../store';

export interface GroupsState {
  groups: { [key: string]: Group },
  status: FetchStatus,
  error?: string,
}

export const initialState: GroupsState = {
  groups: {},
  status: FetchStatus.idle,
};

export const fetchGroups = createAsyncThunk(
  'groups/fetchGroups',
  async (noop, { rejectWithValue, getState }) => {
    const apiEndpoint = constructQueryParams({
      marketplaceId: selectMarketplaceId(getState() as AppState),
    }, GET_GROUPS_API);
    return await customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
    ) as GetAllGroupsResponse;
  },
);

const setGroups = (
  state: GroupsState,
  payload: GetAllGroupsResponse,
) => {
  state.status = FetchStatus.complete;
  const i18n = getI18n();

  const groupsObj: GroupsState['groups'] = payload.groups.reduce((acc, group) => {
    // Filter out groups that don't have translations
    if (!group.nameStringId || !i18n.exists(group.nameStringId)) {
      return acc;
    }

    const { pathname } = new URL(group.avatarSrc);
    return ({
      ...acc,
      [group.id]: {
        ...group,
        avatarSrc: pathname,
      },
    });
  }, {});

  state.groups = groupsObj;
};

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    setGroupsState: (
      state: GroupsState,
      action: PayloadAction<GetAllGroupsResponse>,
    ) => {
      const groupsObj: GroupsState['groups'] = action.payload.groups.reduce((acc, group) => {
        const { pathname } = new URL(group.avatarSrc);
        return ({
          ...acc,
          [group.id]: {
            ...group,
            avatarSrc: pathname,
          },
        });
      }, {});
      state.status = FetchStatus.complete;
      state.groups = groupsObj;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.rejected, (state, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchGroups.pending, (state) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        setGroups(state, action.payload);
      });
  },
});
export const groupsActions = groupsSlice.actions;
export default groupsSlice.reducer;
